import type {
  JobTypeModel,
  ServiceAvailabilityConfigFEModel,
  TimeSlotSettingInfoModel,
  TimeSlotSettingsInfoModel,
} from 'pos-shared/models/serviceAvailabilitySchema.ts';

export type Schedule = {
  id: string;
  day: string;
  time: string;
};

export type AvailableTime = {
  timeslotId: string;
  time: string;
};

export type Availability = {
  day: string;
  availability: AvailableTime[];
};

enum AvailabilityScheduleWeekDay {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

enum AvailabilityTimes {
  AM = 'AM',
  PM = 'PM',
  ALLDAY = 'ALLDAY',
}

const toTimeSlotScheduleModel = (timeSlot: TimeSlotSettingInfoModel) => {
  const values = timeSlot.name?.toUpperCase().split('-');

  if (values?.length !== 2) {
    return null;
  }

  let schedule = null;
  let time = null;

  if (
    Object.values(AvailabilityScheduleWeekDay).includes(
      values[0] as AvailabilityScheduleWeekDay
    )
  ) {
    schedule = values[0];
  }

  if (
    Object.values(AvailabilityTimes).includes(values[1] as AvailabilityTimes)
  ) {
    time = values[1];
  }

  if (schedule == null || time == null) {
    return null;
  }

  return {
    id: timeSlot.id,
    day: schedule,
    time: time,
  } as Schedule;
};

function groupBy<T, K>(array: T[], keyGetter: (item: T) => K): Map<K, T[]> {
  return array.reduce((map, item) => {
    const key = keyGetter(item);
    const group = map.get(key) ?? [];
    return map.set(key, [...group, item]);
  }, new Map<K, T[]>());
}

export const mapJobTypeTimeslotsToServiceAvailability = (
  jobTypes: JobTypeModel[],
  timeSlots: TimeSlotSettingsInfoModel
): ServiceAvailabilityConfigFEModel => {
  const mappedTimeslots = timeSlots
    ?.map(toTimeSlotScheduleModel)
    .filter(Boolean) as Schedule[];

  const groupedTimeslots = groupBy(mappedTimeslots, ({ day }) => day);

  const weekdayOrder = Object.values(AvailabilityScheduleWeekDay) as string[];

  const sortedGroupedTimeslots = new Map(
    [...groupedTimeslots].sort(
      (a, b) => weekdayOrder.indexOf(a[0]) - weekdayOrder.indexOf(b[0])
    )
  );

  const timeOrder = Object.values(AvailabilityTimes) as string[];
  const availableTimeslotSettings: Availability[] = [];
  sortedGroupedTimeslots?.forEach((grp, key) => {
    const times = (grp || []).map((val) => ({
      timeslotId: val.id,
      time: val.time,
    }));
    availableTimeslotSettings.push({
      day: key,
      availability: times.sort(
        (a, b) => timeOrder.indexOf(a.time) - timeOrder.indexOf(b.time)
      ),
    });
  });

  const categories = jobTypes?.map((category) => ({
    productCategoryId: category.id,
    productCategoryTitle: category.name ?? '',
    productCategoryDescription: category.description ?? '',
    productCategoryServiceType: category.serviceType.toString(),
    availabilities: availableTimeslotSettings.map((timeslotSettings) => ({
      day: timeslotSettings.day,
      timeslots: timeslotSettings.availability.map((a) => ({
        timeslotId: a.timeslotId,
        isSelected: false,
        time: a.time,
      })),
    })),
  }));

  return {
    availableTimeslots: availableTimeslotSettings || [],
    categories: categories || [],
  };
};
